import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getStatus, getSession, SUCCESS_STATUSES, FAILURE_STATUSES } from "./api";
import logo from "../../assets/maksu-logo.jpg";
import "./idv.css";

const POLL_EVERY_MS = 3000;
const TIMEOUT_MS = 3 * 60 * 1000;

// Veriff redirects here after the user submits. We poll the backend (which asks
// Veriff) until there is a decision, then go to the success or failure page.
export default function LoadingPage() {
  const navigate = useNavigate();
  const session = getSession();
  const trackingId = session?.trackingId;
  const sessionId = session?.sessionId;
  const [timedOut, setTimedOut] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!trackingId || !sessionId) return;
    let stopped = false;
    let timer;
    const startedAt = Date.now();

    async function check() {
      try {
        const data = await getStatus({ trackingId, sessionId });
        if (stopped) return;
        const status = (data.status || "").toLowerCase();
        const query = `?trackingId=${encodeURIComponent(trackingId)}`;
        if (SUCCESS_STATUSES.includes(status)) {
          return navigate(`/id-verification/success${query}`, { replace: true, state: data });
        }
        if (FAILURE_STATUSES.includes(status)) {
          return navigate(`/id-verification/failure${query}`, { replace: true, state: data });
        }
      } catch (err) {
        if (err.status === 404) {
          if (!stopped) setNotFound(true); // unknown tracking id — stop polling
          return;
        }
        // network blip or Veriff busy — try again on the next tick
      }
      if (stopped) return;
      if (Date.now() - startedAt > TIMEOUT_MS) return setTimedOut(true);
      timer = setTimeout(check, POLL_EVERY_MS);
    }

    check();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [trackingId, sessionId, navigate]);

  // Started on another device (e.g. Veriff handed off from a computer to this
  // phone): this browser has the trackingId from the URL but not the sessionId.
  const handedOff = Boolean(trackingId && !sessionId);
  const waiting = Boolean(sessionId) && !timedOut && !notFound;

  let title = "Checking your ID";
  let text = "Veriff is reviewing your document and selfie. This usually takes under a minute.";
  if (handedOff) {
    title = "You're all done here";
    text = "Your photos were submitted. Go back to the device where you started to see the result.";
  } else if (!session || notFound) {
    title = "No verification found";
    text = "This link doesn't match a verification. Start a new one to continue.";
  } else if (timedOut) {
    title = "Still waiting on Veriff";
    text = "Veriff hasn't sent a decision yet. Refresh this page to keep checking.";
  }

  return (
    <div className="idv-page">
      <div className="idv-card idv-center">
        <img src={logo} alt="MAKSU" className="idv-logo" />
        {waiting && <div className="idv-spinner" aria-hidden="true" />}
        <h1>{title}</h1>
        <p className="idv-text">{text}</p>
        {!waiting && !handedOff && (
          <Link to="/id-verification" className="idv-button">Start again</Link>
        )}
      </div>
    </div>
  );
}